/**
 * Has a guide's patient copy been checked against its official sources?
 * Results live in copy-review.json, written by scripts/review-copy.mjs
 * (Gemini on Vertex AI, australia-southeast1). A result counts only while the
 * copy and sources are unchanged: the hash covers both, so any edit sends the
 * page back out of Google until it is reviewed again. A named clinician
 * review (`reviewedBy`) also counts. Shared by the React pages and the
 * prerender; the fact gate (scripts/fact-check.mjs) enforces it on the sitemap.
 */
import reviews from "./copy-review.json";
import type { ConditionContent } from "./conditions";
import { specialtyContent, type SpecialtyContent } from "./specialty-content";
import { isThinSpecialty } from "./structured-data";

export interface CopyReview {
  hash: string;
  status: "pass" | "fail";
  unsupported: string[];
  /** The sentence quoted from an official source for each claim that passed. */
  evidence?: Array<{ claim: string; quote: string; source: string }>;
  model: string;
  reviewedAt: string;
}

const REVIEWS = reviews as Record<string, CopyReview>;

/** FNV-1a, 32-bit, hex. Change detection only. */
export function copyHash(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** The summary only: the heading names the page ("Knee specialists in
 *  Canberra") and makes no health claim. */
export function conditionReviewText(condition: ConditionContent): string {
  return condition.summary;
}

/** Patient-facing copy only; the GP referral checklist needs a clinician. */
export function specialtyReviewText(copy: SpecialtyContent): string {
  return [copy.patientIntro, ...copy.commonReasons].join("\n");
}

export function reviewKey(kind: "condition" | "specialty", slug: string): string {
  return `${kind}:${slug}`;
}

export function reviewHash(text: string, sources: string[]): string {
  return copyHash(`${text}\n${sources.join("\n")}`);
}

function passed(key: string, text: string, sources: string[]): boolean {
  const review = REVIEWS[key];
  return sources.length > 0 && review?.status === "pass" && review.hash === reviewHash(text, sources);
}

export function isConditionVerified(condition: ConditionContent): boolean {
  return Boolean(condition.reviewedBy) || passed(reviewKey("condition", condition.slug), conditionReviewText(condition), condition.sources);
}

/** Specialties without guide copy have nothing to check. */
export function isSpecialtyVerified(slug: string, copy: SpecialtyContent | null | undefined): boolean {
  if (!copy) return true;
  return Boolean(copy.reviewedBy) || passed(reviewKey("specialty", slug), specialtyReviewText(copy), copy.sources);
}

/** Specialty pages stay out of Google when thin or when their copy is unverified. */
export function specialtyPageNoindex(slug: string, count: number): boolean {
  return isThinSpecialty(count) || !isSpecialtyVerified(slug, specialtyContent(slug));
}
